import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import TwoFactorModal from './components/TwoFactorModal';

const AuthPage = () => {
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'forgot'
  const [form, setForm] = useState({ fullName: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 2FA state
  const [twoFAPending, setTwoFAPending] = useState(false);
  const [twoFASettings, setTwoFASettings] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);

  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = location?.state?.from || '/account-dashboard';

  useEffect(() => {
    if (user && !twoFAPending) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, twoFAPending, navigate, redirectTo]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e?.target?.value }));
    setError('');
  };

  // Generate device fingerprint (same logic as TwoFactorModal)
  const generateDeviceFingerprint = () => {
    const ua = navigator?.userAgent || '';
    const lang = navigator?.language || '';
    const tz = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || '';
    const screen = `${window?.screen?.width}x${window?.screen?.height}`;
    const raw = `${ua}|${lang}|${tz}|${screen}`;
    let hash = 0;
    for (let i = 0; i < raw?.length; i++) {
      const char = raw?.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return `fp_${Math.abs(hash)?.toString(36)}_${raw?.length?.toString(36)}`;
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!form?.email || !form?.password) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    setLoading(true);
    setError('');
    const { data, error: err } = await signIn(form?.email, form?.password);
    if (err) {
      const msg = typeof err === 'string' && err?.trim() !== '' ? err : err?.message || 'Une erreur est survenue. Veuillez réessayer.';
      setError(msg);
      setLoading(false);
      return;
    }

    // Check if 2FA is enabled for this user
    const userId = data?.user?.id;
    if (userId) {
      try {
        const { data: twoFAData } = await supabase?.
        from('user_2fa_settings')?.
        select('*')?.
        eq('user_id', userId)?.
        eq('is_enabled', true)?.
        single();

        if (twoFAData) {
          // Check if current device is trusted
          const fingerprint = generateDeviceFingerprint();
          const { data: trustedData } = await supabase?.
          from('trusted_devices')?.
          select('id')?.
          eq('user_id', userId)?.
          eq('device_fingerprint', fingerprint)?.
          eq('is_active', true)?.
          single();

          if (trustedData) {
            // Trusted device — bypass 2FA, navigate directly
            setLoading(false);
            navigate(redirectTo, { replace: true });
            return;
          }

          // 2FA required — show modal
          setTwoFASettings(twoFAData);
          setPendingUserId(userId);
          setTwoFAPending(true);
          setLoading(false);
          return;
        }
      } catch {


        // No 2FA settings found or error — proceed normally
      }}
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e?.preventDefault();
    if (!form?.fullName || !form?.email || !form?.password || !form?.confirmPassword) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    if (form?.password?.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (form?.password !== form?.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    setError('');
    const { error: err } = await signUp(form?.email, form?.password, { full_name: form?.fullName });
    if (err) {
      const msg = typeof err === 'string' && err?.trim() !== '' ? err : err?.message || 'Une erreur est survenue lors de la création du compte. Veuillez réessayer.';
      setError(msg);
      setLoading(false);
    } else {
      setSuccess('Compte créé ! Un email de confirmation a été envoyé à ' + form?.email + '. Cliquez sur le lien dans l\'email pour activer votre compte avant de vous connecter.');
      setLoading(false);
      setMode('login');
    }
  };

  const handleForgot = async (e) => {
    e?.preventDefault();
    if (!form?.email) {
      setError('Veuillez entrer votre adresse email.');
      return;
    }
    setLoading(true);
    setError('');
    // Use supabase directly for password reset
    const { supabase } = await import('../../lib/supabase');
    const { error: err } = await supabase?.auth?.resetPasswordForEmail(form?.email, {
      redirectTo: `${window?.location?.origin}/account-dashboard`
    });
    setLoading(false);
    if (err) {
      setError(err?.message);
    } else {
      setSuccess('Un lien de réinitialisation a été envoyé à votre email.');
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
    setForm({ fullName: '', email: '', password: '', confirmPassword: '' });
  };

  const handle2FAVerified = () => {
    setTwoFAPending(false);
    navigate(redirectTo, { replace: true });
  };

  const handle2FACancel = async () => {
    // Sign out the user since they cancelled 2FA
    const { supabase: sb } = await import('../../lib/supabase');
    await sb?.auth?.signOut();
    setTwoFAPending(false);
    setTwoFASettings(null);
    setPendingUserId(null);
  };

  return (
    <>
      {/* 2FA Modal overlay */}
      {twoFAPending && twoFASettings &&
      <TwoFactorModal
        userId={pendingUserId}
        twoFASettings={twoFASettings}
        onVerified={handle2FAVerified}
        onCancel={handle2FACancel} />

      }

      <Helmet>
        <title>{mode === 'signup' ? 'Créer un compte' : 'Connexion'} – Kapital States</title>
        <meta name="description" content="Connectez-vous ou créez votre compte Kapital States pour gérer vos propriétés, alertes et rendez-vous." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-surface via-white to-brand-gold/5 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5">
          <Link to="/homepage" className="flex items-center space-x-3 group">
            <div className="w-9 h-9 bg-gradient-to-br from-brand-gold to-brand-earth rounded-xl flex items-center justify-center shadow-ios-light group-hover:scale-105 transition-transform duration-300">
              <Icon name="Building2" size={20} color="white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col">
              <span className="font-display font-display-semibold text-ios-title3 text-brand-earth leading-tight tracking-tight">
                Kapital States
              </span>
              <span className="text-ios-caption1 text-text-secondary font-system leading-tight">
                Élever l'Immobilier
              </span>
            </div>
          </Link>
          <Link
            to="/homepage"
            className="flex items-center space-x-1.5 px-3.5 py-2 rounded-xl bg-white border border-border/50 text-ios-callout font-system font-body-medium text-text-secondary hover:text-brand-earth hover:border-brand-gold/40 transition-all shadow-ios-light">

            <Icon name="ArrowLeft" size={14} />
            <span>Retour</span>
          </Link>
        </div>

        {/* Main Card */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-md">
            {/* Card */}
            <div className="bg-white rounded-3xl shadow-ios-large border border-border/30 overflow-hidden">
              {/* Mode Tabs */}
              {mode !== 'forgot' &&
              <div className="flex border-b border-border/40">
                  <button
                  onClick={() => switchMode('login')}
                  className={`flex-1 py-4 text-ios-callout font-system font-body-semibold transition-all duration-200 ${
                  mode === 'login' ? 'text-brand-earth border-b-2 border-brand-earth bg-brand-gold/5' : 'text-text-secondary hover:text-text-primary'}`
                  }>

                    Connexion
                  </button>
                  <button
                  onClick={() => switchMode('signup')}
                  className={`flex-1 py-4 text-ios-callout font-system font-body-semibold transition-all duration-200 ${
                  mode === 'signup' ? 'text-brand-earth border-b-2 border-brand-earth bg-brand-gold/5' : 'text-text-secondary hover:text-text-primary'}`
                  }>

                    Inscription
                  </button>
                </div>
              }

              <div className="px-7 py-8 border-none">
                {/* Title */}
                <div className="mb-7">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-gold/20 to-brand-earth/10 flex items-center justify-center mb-4">
                    <Icon
                      name={mode === 'signup' ? 'UserPlus' : mode === 'forgot' ? 'KeyRound' : 'LogIn'}
                      size={22}
                      className="text-brand-earth" />

                  </div>
                  <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth leading-tight">
                    {mode === 'login' && 'Bon retour !'}
                    {mode === 'signup' && 'Créer un compte'}
                    {mode === 'forgot' && 'Mot de passe oublié'}
                  </h1>
                  <p className="text-ios-callout text-text-secondary mt-1.5 font-system">
                    {mode === 'login' && 'Connectez-vous pour accéder à votre espace personnel.'}
                    {mode === 'signup' && 'Rejoignez Kapital States et gérez vos propriétés.'}
                    {mode === 'forgot' && 'Entrez votre email pour recevoir un lien de réinitialisation.'}
                  </p>
                </div>

                {/* Success message */}
                {success &&
                <div className="flex items-start space-x-2.5 p-4 bg-green-50 border border-green-100 rounded-2xl mb-5">
                    <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-ios-callout text-green-700 font-system">{success}</p>
                  </div>
                }

                {/* Error message */}
                {error &&
                <div className="flex items-start space-x-2.5 p-4 bg-red-50 border border-red-100 rounded-2xl mb-5">
                    <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-ios-callout text-red-600 font-system">{error}</p>
                  </div>
                }

                {/* LOGIN FORM */}
                {mode === 'login' &&
                <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                        Adresse email
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <Icon name="Mail" size={16} className="text-text-secondary" />
                        </div>
                        <input
                        type="email"
                        value={form?.email}
                        onChange={handleChange('email')}
                        placeholder="votre@email.com"
                        autoComplete="email"
                        className="w-full pl-10 pr-4 py-3.5 border border-border rounded-xl text-ios-callout font-system text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200 bg-surface/30" />

                      </div>
                    </div>

                    <div>
                      <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                        Mot de passe
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <Icon name="Lock" size={16} className="text-text-secondary" />
                        </div>
                        <input
                        type={showPassword ? 'text' : 'password'}
                        value={form?.password}
                        onChange={handleChange('password')}
                        placeholder="Votre mot de passe"
                        autoComplete="current-password"
                        className="w-full pl-10 pr-11 py-3.5 border border-border rounded-xl text-ios-callout font-system text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200 bg-surface/30" />

                        <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 p-0.5 text-text-secondary hover:text-text-primary transition-colors">

                          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="text-ios-caption1 text-brand-earth hover:underline font-system font-body-medium">

                        Mot de passe oublié ?
                      </button>
                    </div>

                    <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center space-x-2.5 py-3.5 bg-gradient-to-r from-brand-earth to-brand-earth/90 text-white rounded-xl text-ios-callout font-system font-body-semibold hover:from-brand-earth/95 hover:to-brand-earth/85 disabled:opacity-60 transition-all duration-200 shadow-ios-light mt-2">

                      {loading ?
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :

                    <>
                          <Icon name="LogIn" size={17} color="white" />
                          <span>Se connecter</span>
                        </>
                    }
                    </button>
                  </form>
                }

                {/* SIGNUP FORM */}
                {mode === 'signup' &&
                <form onSubmit={handleSignup} className="space-y-4">
                    <div>
                      <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                        Nom complet
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <Icon name="User" size={16} className="text-text-secondary" />
                        </div>
                        <input
                        type="text"
                        value={form?.fullName}
                        onChange={handleChange('fullName')}
                        placeholder="Votre nom complet"
                        autoComplete="name"
                        className="w-full pl-10 pr-4 py-3.5 border border-border rounded-xl text-ios-callout font-system text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200 bg-surface/30" />

                      </div>
                    </div>

                    <div>
                      <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                        Adresse email
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <Icon name="Mail" size={16} className="text-text-secondary" />
                        </div>
                        <input
                        type="email"
                        value={form?.email}
                        onChange={handleChange('email')}
                        placeholder="votre@email.com"
                        autoComplete="email"
                        className="w-full pl-10 pr-4 py-3.5 border border-border rounded-xl text-ios-callout font-system text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200 bg-surface/30" />

                      </div>
                    </div>

                    <div>
                      <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                        Mot de passe
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <Icon name="Lock" size={16} className="text-text-secondary" />
                        </div>
                        <input
                        type={showPassword ? 'text' : 'password'}
                        value={form?.password}
                        onChange={handleChange('password')}
                        placeholder="Minimum 8 caractères"
                        autoComplete="new-password"
                        className="w-full pl-10 pr-11 py-3.5 border border-border rounded-xl text-ios-callout font-system text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200 bg-surface/30" />

                        <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 p-0.5 text-text-secondary hover:text-text-primary transition-colors">

                          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                        Confirmer le mot de passe
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <Icon name="ShieldCheck" size={16} className="text-text-secondary" />
                        </div>
                        <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={form?.confirmPassword}
                        onChange={handleChange('confirmPassword')}
                        placeholder="Répétez le mot de passe"
                        autoComplete="new-password"
                        className="w-full pl-10 pr-11 py-3.5 border border-border rounded-xl text-ios-callout font-system text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200 bg-surface/30" />

                        <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 p-0.5 text-text-secondary hover:text-text-primary transition-colors">

                          <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={16} />
                        </button>
                      </div>
                    </div>

                    <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center space-x-2.5 py-3.5 bg-gradient-to-r from-brand-earth to-brand-earth/90 text-white rounded-xl text-ios-callout font-system font-body-semibold hover:from-brand-earth/95 hover:to-brand-earth/85 disabled:opacity-60 transition-all duration-200 shadow-ios-light mt-2">

                      {loading ?
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :

                    <>
                          <Icon name="UserPlus" size={17} color="white" />
                          <span>Créer mon compte</span>
                        </>
                    }
                    </button>

                    <p className="text-ios-caption1 text-text-secondary text-center font-system mt-2">
                      En créant un compte, vous acceptez nos{' '}
                      <Link to="/legal-compliance-footer" className="text-brand-earth hover:underline">
                        conditions d'utilisation
                      </Link>
                      .
                    </p>
                  </form>
                }

                {/* FORGOT PASSWORD FORM */}
                {mode === 'forgot' &&
                <form onSubmit={handleForgot} className="space-y-4">
                    <div>
                      <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-1.5">
                        Adresse email
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <Icon name="Mail" size={16} className="text-text-secondary" />
                        </div>
                        <input
                        type="email"
                        value={form?.email}
                        onChange={handleChange('email')}
                        placeholder="votre@email.com"
                        autoComplete="email"
                        className="w-full pl-10 pr-4 py-3.5 border border-border rounded-xl text-ios-callout font-system text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200 bg-surface/30" />

                      </div>
                    </div>

                    <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center space-x-2.5 py-3.5 bg-gradient-to-r from-brand-earth to-brand-earth/90 text-white rounded-xl text-ios-callout font-system font-body-semibold disabled:opacity-60 transition-all duration-200 shadow-ios-light">

                      {loading ?
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :

                    <>
                          <Icon name="Send" size={17} color="white" />
                          <span>Envoyer le lien</span>
                        </>
                    }
                    </button>

                    <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className="w-full flex items-center justify-center space-x-1.5 py-2.5 text-ios-callout font-system font-body-medium text-text-secondary hover:text-brand-earth transition-colors">

                      <Icon name="ArrowLeft" size={14} />
                      <span>Retour à la connexion</span>
                    </button>
                  </form>
                }
              </div>
            </div>

            {/* Benefits */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
              { icon: 'Heart', label: 'Favoris sauvegardés' },
              { icon: 'Bell', label: 'Alertes propriétés' },
              { icon: 'Calendar', label: 'Historique RDV' }]?.
              map((item) =>
              <div key={item?.label} className="bg-white/70 backdrop-blur-sm rounded-2xl p-3.5 text-center border border-border/30 shadow-ios-light border-none">
                  <div className="w-8 h-8 rounded-xl bg-brand-gold/15 flex items-center justify-center mx-auto mb-2">
                    <Icon name={item?.icon} size={16} className="text-brand-earth" />
                  </div>
                  <p className="text-[11px] font-system font-body-medium text-text-secondary leading-tight">{item?.label}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default AuthPage;